import { Box, Typography } from "@mui/material";
import { type FC, type ReactNode, useState } from "react";
import type { MetaChipTone } from "../Badges/MetaChip";
import { NameDisclosureList } from "../molecules/NameDisclosureList";
import SystemDialog from "./Dialog";

/** One named thing blocking the action, e.g. an employee, with an optional qualifying tag. */
export type ConflictItem = {
	/**
	 * Stable identity for React reconciliation. Pass one whenever the caller has it (a nurse id), since
	 * `label` is NOT unique — two employees genuinely share a name, and keying on the label collides.
	 * Falls back to the list index when absent.
	 */
	id?: string | number;
	/** Already-translated primary label — usually a person's or entity's name. */
	label: string;
	/** Optional short annotation, e.g. "only job" vs "has another job". Already translated. */
	tag?: string;
	/** Semantic tone for `tag`. Default `neutral`. */
	tagTone?: MetaChipTone;
};

/**
 * One category of blocker: a count, plus optionally the named things behind it.
 *
 * `count` is always the headline. `items` is optional detail — when present it goes behind a
 * disclosure rather than inline, because a category can hold hundreds of rows (188 employees) and the
 * count is what the reader actually needs.
 */
export type ConflictGroup = {
	/** Already-translated category name, e.g. "Active employees" or "Scheduled shifts". */
	label: string;
	count: number;
	items?: ConflictItem[];
	/** Already-translated disclosure label, e.g. "Show 188 employees". Required to reveal `items`. */
	itemsToggleLabel?: string;
	/** Already-translated label while open, e.g. "Hide employees". Falls back to `itemsToggleLabel`. */
	itemsHideLabel?: string;
};

type Props = {
	open: boolean;
	onClose: () => void;
	/** Already-translated title. Name the thing that can't be removed, e.g. "Aide is still in use". */
	title: ReactNode;
	/** Already-translated explanation of why the action was refused and what to do about it. */
	description: ReactNode;
	/** The blockers. Groups with a zero count are dropped, so callers can pass every category. */
	groups: ConflictGroup[];
	/** Already-translated dismiss label, e.g. "Got it". */
	dismissLabel: ReactNode;
	/**
	 * Item count at or below which a group's names start REVEALED. A short list is the actionable case
	 * — naming two blocking employees is the whole point — so hiding it behind a click is friction;
	 * a long list is a reference the reader opts into. Default 5; pass 0 to always start collapsed.
	 */
	autoExpandThreshold?: number;
	dataTestId?: string;
};

/**
 * Explains why an action was refused: the thing being removed is still depended on, and here is
 * exactly what depends on it. **Informational only** — a single dismiss button, no confirm and no
 * override, for flows where forcing the action through would leave data in a state nobody chose.
 *
 * Presents the blockers as a **KPI row of counts**, with names behind a per-group disclosure. Two
 * deliberate choices behind that:
 * - The counts are the information. A category can hold hundreds of rows, and showing 8 of 188 names
 *   inline implies a checklist the reader can work through when they can't.
 * - The tiles are **equal weight and untinted**. They measure different things (employees vs shifts),
 *   so they must not be compared to each other — no shared scale, no size-by-magnitude, and no
 *   severity tint, which would misread as a status the app doesn't define here.
 *
 * Generic over any "cannot delete/disable X, these depend on it" case (de-enabling a job, removing a
 * location, retiring a role): the caller supplies translated copy and the grouped blockers, this owns
 * only the presentation. Composes `SystemDialog` (`hideCancel`) and `NameDisclosureList`, so it
 * inherits the app's modal and disclosure language rather than restating it.
 */
export const ConflictBlockedDialog: FC<Props> = ({
	open,
	onClose,
	title,
	description,
	groups,
	dismissLabel,
	autoExpandThreshold = 5,
	dataTestId = "conflict-blocked-dialog",
}) => {
	const [expanded, setExpanded] = useState<Set<string> | null>(null);
	const visibleGroups = groups.filter((group) => group.count > 0);
	// Only a group with names AND a label to reveal them gets a disclosure; narrowed here so the list below
	// needs no fallbacks for either.
	const disclosureGroups = visibleGroups.flatMap((group) =>
		group.items?.length && group.itemsToggleLabel ? [{ ...group, items: group.items, itemsToggleLabel: group.itemsToggleLabel }] : [],
	);

	// Derived on first render rather than in state, so a fresh payload re-evaluates the default.
	const defaultExpanded = new Set(groups.filter((g) => (g.items?.length ?? 0) > 0 && (g.items?.length ?? 0) <= autoExpandThreshold).map((g) => g.label));
	const openGroups = expanded ?? defaultExpanded;

	const toggle = (label: string) =>
		setExpanded((prev) => {
			const next = new Set(prev ?? defaultExpanded);
			if (next.has(label)) next.delete(label);
			else next.add(label);
			return next;
		});

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			title={title}
			dataTestId={dataTestId}
			hideCancel
			confirm={dismissLabel}
			confirmButtonVariant="primary"
			onConfirm={onClose}
			body={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
					<Typography variant="body2" sx={{ color: "text.secondary" }} data-testid={`${dataTestId}-description`}>
						{description}
					</Typography>

					{/* KPI row as a GRID, not flex-wrap: equal-width columns that wrap uniformly. With
					    `flex: 1 1 auto` a lone item on the wrapped row absorbed the full width (measured 552px
					    against ~130px siblings), and label length made the others unequal — both of which read as
					    broken in a row whose whole job is to be scannable. Wraps rather than scrolls, so no count
					    is ever hidden off-edge. */}
					<Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(116px, 1fr))", gap: 1 }} data-testid={`${dataTestId}-tiles`}>
						{visibleGroups.map((group) => (
							<Box
								key={group.label}
								data-testid={`${dataTestId}-tile`}
								sx={{
									px: 1.5,
									py: 1.25,
									borderRadius: 2,
									backgroundColor: "action.hover",
									display: "flex",
									flexDirection: "column",
									gap: 0.25,
								}}
							>
								{/* Value in text ink, never a status colour — these are magnitudes, not states. */}
								<Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, color: "text.primary" }} data-testid={`${dataTestId}-tile-value`}>
									{group.count.toLocaleString()}
								</Typography>
								<Typography variant="caption" sx={{ color: "text.secondary" }}>
									{group.label}
								</Typography>
							</Box>
						))}
					</Box>

					{disclosureGroups.map((group) => (
						<NameDisclosureList
							key={`${group.label}-items`}
							open={openGroups.has(group.label)}
							onToggle={() => toggle(group.label)}
							showLabel={group.itemsToggleLabel}
							hideLabel={group.itemsHideLabel ?? group.itemsToggleLabel}
							sections={[{ id: group.label, items: group.items }]}
							dataTestId={`${dataTestId}-group`}
							toggleTestId={`${dataTestId}-toggle`}
							itemTestId={() => `${dataTestId}-item`}
						/>
					))}
				</Box>
			}
		/>
	);
};

export default ConflictBlockedDialog;
